// src/pages/WeddingAtlasPage.jsx
// Wedding Atlas, public directory of all destination wedding guides.
// URL: /wedding-guides
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { supabase } from "../lib/supabaseClient";
import HomeNav from "../components/nav/HomeNav";
import { useBreakpoint } from "../hooks/useWindowWidth";
import { useInView } from "../hooks/useInView";
import EditorialCard from "../components/cards/EditorialCard";
import { track } from "../utils/track";
import { usePrerenderReady } from "../utils/prerenderReady";

const GD     = "var(--font-heading-primary)";
const NU     = "var(--font-body)";
const GOLD   = "#c4a35a";
const BG     = "#FAF8F4";
const BG_ALT = "#F5F2EC";
const TXT    = "#2a1f0e";
const dim    = (a) => `rgba(42,31,14,${a})`;

const PREVIEW_COUNT = 6;
const COUNTRY_ORDER = ["Italy", "United Kingdom", "France", "Greece", "Portugal", "Spain", "UAE", "Switzerland", "Morocco", "Croatia", "Sri Lanka", "Bali", "Austria", "Montenegro", "Malta", "United States"];

const COUNTRY_ADJECTIVE = {
  "Italy":          "Italian",
  "United Kingdom": "UK",
  "France":         "French",
  "Greece":         "Greek",
  "Portugal":       "Portuguese",
  "Spain":          "Spanish",
  "UAE":            "UAE",
  "Switzerland":    "Swiss",
  "Morocco":        "Moroccan",
  "Croatia":        "Croatian",
  "Sri Lanka":      "Sri Lankan",
  "Bali":           "Balinese",
  "Austria":        "Austrian",
  "Montenegro":     "Montenegrin",
  "Malta":          "Maltese",
  "United States":  "American",
};

const COUNTRY_ATLAS_SLUG = {
  "Italy":          "italy",
  "United Kingdom": "united-kingdom",
  "France":         "france",
  "Greece":         "greece",
  "Portugal":       "portugal",
  "Spain":          "spain",
  "UAE":            "uae",
  "Switzerland":    "switzerland",
  "Morocco":        "morocco",
  "Croatia":        "croatia",
  "Sri Lanka":      "sri-lanka",
  "Bali":           "bali",
  "Austria":        "austria",
  "Montenegro":     "montenegro",
  "Malta":          "malta",
  "United States":  "united-states",
};

const COUNTRY_INTROS = {
  "Italy":
    "From the shores of Lake Como to the cliffs of the Amalfi Coast, Italy remains the world's most sought-after destination for luxury weddings. Villa gardens, lakeside palazzi, Sicilian estates, Venetian piano nobile rooms. No other country concentrates this level of beauty, history, and hospitality in a single geography.",
  "United Kingdom":
    "Castle estates, Cotswold manor houses, Highland lochs, and Cornish cliff-tops. Britain offers the widest range of wedding venue character of any country in Europe. The finest properties combine centuries of architectural history with a standard of hospitality that is entirely contemporary.",
  "France":
    "Provencal chateaux, Loire Valley estates, and the refined grandeur of the French countryside have drawn couples from across the world for generations. France rewards those who plan carefully: the venues at the top of the market are among the most beautiful in existence.",
  "Greece":
    "Whitewashed clifftop chapels, Aegean light, and a warmth of place that every other Mediterranean destination tries to imitate. Greece has been the backdrop for significant weddings for three thousand years. The best of the modern luxury venues have learned to honour that.",
  "Portugal":
    "Atlantic manor estates, the vine terraces of the Douro Valley, and the warm stone of the Alentejo plains. Portugal offers exceptional value at the top end of the market and a hospitality culture that is genuinely warm rather than merely professional.",
  "Spain":
    "Andalusian fincas, Balearic coastlines, and the great paradores of Castile. Spain is a country of dramatic contrasts: from intimate whitewashed cortijos in the hills above Granada to vast seaside estates on the Costa Brava.",
  "UAE":
    "From the Burj Khalifa to a private island wildlife reserve, the UAE offers the widest range of luxury wedding environments of any single country. Downtown Dubai, the Palm Jumeirah, the Hajar Mountains of Ras Al Khaimah, and the desert outside the city: nine distinct destinations within two hours of a single airport.",
  "Switzerland":
    "Clifftop resort above Lake Lucerne, the Matterhorn, Badrutt's Palace, the UNESCO Lavaux vineyards. Switzerland concentrates more immediately recognisable luxury imagery per square kilometre than any other country in Europe. The train network, the food, and the light at altitude make it one of the most complete destination wedding countries in the world.",
  "Morocco":
    "La Mamounia, the Royal Mansour, Kasbah du Toubkal above Imlil, sand dunes at Erg Chebbi. Morocco offers a depth of visual contrast that no other North African destination approaches: medina riads, Atlas Mountain kasbahs, Atlantic coast ramparts, and Saharan luxury camps within a single country.",
  "Croatia":
    "The old city of Dubrovnik enclosed by its walls, Hvar harbour at dusk, the Zlatni Rat beach on Brac, Istrian hill towns above the Adriatic. Croatia is the fastest-rising luxury destination in Europe, and the best venues are booking three years ahead.",
  "Sri Lanka":
    "Amangalla in Galle Fort, Ceylon Tea Trails across five historic bungalows in the hill country, clifftop suites above the Indian Ocean. Sri Lanka is one of the most under-represented destination wedding countries in the world relative to the quality of its venues.",
  "Bali":
    "The Four Seasons at Sayan above the Ayung River gorge, the BVLGARI Resort at Uluwatu, Mandapa in the jungle above Ubud. Bali has more internationally recognised luxury wedding venues per square kilometre than any island in the world.",
  "Austria":
    "Palais Coburg in Vienna, Schloss Leopoldskron in Salzburg, the Kempinski in the Tyrol, Schloss Fuschl on the Salzkammergut lakes. Austria is the most underestimated luxury destination in central Europe: imperial grandeur, precision hospitality, and landscape of the highest order.",
  "Montenegro":
    "Aman Sveti Stefan on its own island, Palazzo Radomiri on the Bay of Kotor, Regent Porto Montenegro at Europe's finest superyacht marina. Montenegro has more luxury hotel infrastructure per kilometre of coastline than anywhere in the Mediterranean, and it remains genuinely undiscovered.",
  "Malta":
    "The Xara Palace within the walls of Mdina, Ta'Cenc above the Gozitan cliffs, Palazzo Parisio in Naxxar. Malta is unique: a sovereign island nation of extraordinary historical density, three hours from London, with legal marriage available at historic palaces and baroque chapels that cannot be replicated anywhere else.",
  "United States":
    "The vineyard estates of Napa Valley, oceanfront mansions in the Hamptons, the cliffs above the Pacific at Big Sur, and the grand ballrooms of The Plaza and The Breakers. America's luxury wedding landscape spans from Charleston's antebellum plantation houses to Maui's lava coast, from Aspen's wildflower meadows to Sedona's red rock formations. No other country offers this range of landscape, climate, and venue character within a single destination.",
};

const FEATURED_SLUGS = [
  "positano-wedding-guide",
  "siena-wedding-guide",
  "bellagio-wedding-guide",
];

function countrySort(a, b) {
  const ai = COUNTRY_ORDER.indexOf(a);
  const bi = COUNTRY_ORDER.indexOf(b);
  if (ai === -1 && bi === -1) return a.localeCompare(b);
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
}

// ── Hero bottom-right link ────────────────────────────────────────────────────
function HeroCollectionLink({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none", border: "none", padding: 0,
        cursor: "pointer", display: "inline-flex",
        alignItems: "center", gap: 10,
        flexShrink: 0,
      }}
    >
      <span style={{
        fontFamily: NU, fontSize: 11, fontWeight: 600,
        letterSpacing: "0.22em", textTransform: "uppercase",
        color: hovered ? TXT : dim(0.38),
        transition: "color 0.2s",
      }}>
        View the full collection
      </span>
      <span style={{
        fontFamily: NU, fontSize: 12,
        color: hovered ? GOLD : dim(0.25),
        transform: hovered ? "translateX(4px)" : "translateX(0)",
        transition: "color 0.2s, transform 0.2s ease",
        display: "inline-block",
      }}>
        &rarr;
      </span>
    </button>
  );
}

// ── Chapter heading ───────────────────────────────────────────────────────────
function CountryHeader({ country, count, isMobile }) {
  const ref     = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} style={{
      marginBottom: isMobile ? 36 : 44,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(12px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
    }}>
      <div style={{
        display: "flex", alignItems: "baseline",
        justifyContent: "space-between", marginBottom: 16,
      }}>
        <h2 style={{
          fontFamily: GD, fontSize: isMobile ? 32 : 44,
          fontWeight: 400, color: TXT,
          margin: 0, lineHeight: 1, letterSpacing: "-0.01em",
        }}>
          {country}
        </h2>
        <span style={{
          fontFamily: NU, fontSize: isMobile ? 11 : 12,
          letterSpacing: "0.16em", textTransform: "uppercase",
          color: dim(0.35), flexShrink: 0, paddingLeft: 16,
        }}>
          {count} {count === 1 ? "guide" : "guides"}
        </span>
      </div>
      <div style={{
        height: 1,
        background: `linear-gradient(90deg, ${GOLD} 0%, rgba(196,163,90,0.25) 60%, transparent 100%)`,
        marginBottom: 20,
      }} />
      {COUNTRY_INTROS[country] && (
        <p style={{
          fontFamily: NU, fontSize: isMobile ? 14 : 15,
          color: dim(0.62), lineHeight: 1.8,
          margin: 0, maxWidth: 680,
        }}>
          {COUNTRY_INTROS[country]}
        </p>
      )}
    </div>
  );
}

// ── Featured section header ───────────────────────────────────────────────────
function FeaturedHeader({ isMobile }) {
  const ref     = useRef(null);
  const visible = useInView(ref);
  return (
    <div ref={ref} style={{
      marginBottom: isMobile ? 40 : 52,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(12px)",
      transition: "opacity 0.5s ease, transform 0.5s ease",
    }}>
      <p style={{
        fontFamily: NU, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.28em", textTransform: "uppercase",
        color: dim(0.32), margin: "0 0 14px",
      }}>
        Featured Destinations
      </p>
      <p style={{
        fontFamily: GD, fontSize: isMobile ? 28 : 38,
        fontWeight: 400, fontStyle: "italic",
        color: TXT, margin: 0, lineHeight: 1.2,
      }}>
        The most sought-after in the collection.
      </p>
    </div>
  );
}

// ── Country CTAs: expand + future country atlas link ──────────────────────────
function CountryCTAs({ country, total, isExpanded, onExpand, onCollapse, isMobile }) {
  const ref     = useRef(null);
  const visible = useInView(ref);
  const adj     = COUNTRY_ADJECTIVE[country] || country;

  return (
    <div ref={ref} style={{
      marginTop: isMobile ? 36 : 44,
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "flex-start" : "center",
      gap: isMobile ? 16 : 36,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(8px)",
      transition: "opacity 0.45s ease 0.1s, transform 0.45s ease 0.1s",
    }}>

      {!isExpanded && total > PREVIEW_COUNT && (
        <ExpandButton
          label={`View all ${total} ${adj} destinations`}
          arrow="→"
          onClick={onExpand}
        />
      )}

      {isExpanded && (
        <ExpandButton
          label="Show less"
          arrow="↑"
          onClick={onCollapse}
        />
      )}

      {COUNTRY_ATLAS_SLUG[country] && (
        <span
          title="Country guide pages coming soon"
          style={{
            fontFamily: NU, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.14em", textTransform: "uppercase",
            color: dim(0.25), cursor: "default", userSelect: "none",
          }}
        >
          Explore {country} &rarr;
        </span>
      )}

    </div>
  );
}

function ExpandButton({ label, arrow = "→", onClick }) {
  const [hovered, setHovered] = useState(false);
  const isCollapse = arrow === "↑";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none", border: "none", padding: 0,
        cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
      }}
    >
      <span style={{
        fontFamily: NU, fontSize: 12, fontWeight: 600,
        letterSpacing: "0.14em", textTransform: "uppercase",
        color: hovered ? TXT : dim(0.52),
        transition: "color 0.2s",
      }}>
        {label}
      </span>
      <span style={{
        display: "inline-block",
        transform: isCollapse
          ? (hovered ? "translateY(-2px)" : "translateY(0)")
          : (hovered ? "translateX(4px)" : "translateX(0)"),
        transition: "transform 0.2s ease",
        color: hovered ? GOLD : dim(0.35),
        fontSize: 14,
      }}>
        {arrow}
      </span>
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function WeddingAtlasPage({
  onNavigateGuide,
  onNavigateAbout,
  onNavigateStandard,
  onVendorLogin,
  darkMode,
  onToggleDark,
}) {
  const { isMobile } = useBreakpoint();
  const [guides,            setGuides]            = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [expandedCountries, setExpandedCountries] = useState({});
  const heroRef        = useRef(null);
  const heroVisible    = useInView(heroRef, "0px");
  const statsRef       = useRef(null);
  const statsVisible   = useInView(statsRef, "0px");
  const collectionRef  = useRef(null);

  // Signal the prerenderer only once the guides are actually in hand — without this the
  // Atlas index snapshots as an empty shell (the gap that made every guide route 404).
  usePrerenderReady(!loading && guides.length > 0);

  useEffect(() => {
    supabase
      .from("region_guides")
      .select("slug, name, country, hero_image, created_at")
      .eq("published", true)
      .order("country")
      .order("name")
      .then(({ data }) => {
        setGuides(data || []);
        setLoading(false);
      });
  }, []);

  const cols3 = isMobile ? 1 : 3;

  const byCountry = {};
  guides.forEach(g => {
    const c = g.country || "Other";
    if (!byCountry[c]) byCountry[c] = [];
    byCountry[c].push(g);
  });
  const countries = Object.keys(byCountry).sort(countrySort);

  const featuredGuides = FEATURED_SLUGS
    .map(s => guides.find(g => g.slug === s))
    .filter(Boolean);

  function navigate(slug) {
    onNavigateGuide?.(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function expandCountry(country) {
    setExpandedCountries(prev => ({ ...prev, [country]: true }));
    track("editorial_section_expand", {
      country,
      collection: "atlas",
      context: `${(COUNTRY_ATLAS_SLUG[country] || country.toLowerCase())}_grid`,
      source: "wedding_guides",
    });
  }

  function collapseCountry(country) {
    setExpandedCountries(prev => ({ ...prev, [country]: false }));
  }

  return (
    <>
      <Helmet>
        <title>Wedding Atlas, Destination Wedding Guides | Luxury Wedding Directory</title>
        <meta name="description" content="Browse destination wedding guides for Italy, the UK, France and beyond. Expert venue intelligence, transport, costs, and seasonal advice for every region." />
        <link rel="canonical" href="https://luxuryweddingdirectory.com/wedding-guides" />
        <meta property="og:title" content="Wedding Atlas, Destination Wedding Guides" />
        <meta property="og:description" content="Expert guides to the world's finest wedding destinations." />
        <meta property="og:type" content="website" />
      </Helmet>

      <HomeNav
        hasHero={false}
        onNavigateAbout={onNavigateAbout}
        onNavigateStandard={onNavigateStandard}
        onVendorLogin={onVendorLogin}
        darkMode={darkMode}
        onToggleDark={onToggleDark}
      />

      <div style={{ background: BG, minHeight: "100vh", fontFamily: NU }}>

        {/* ── HERO ──────────────────────────────────────────────────────────── */}
        <section style={{
          padding: isMobile ? "104px 24px 80px" : "168px 64px 96px",
          boxSizing: "border-box",
        }}>
          <div style={{ maxWidth: 760, margin: "0 auto" }}>
            <div
              ref={heroRef}
              style={{
                opacity: heroVisible ? 1 : 0,
                transform: heroVisible ? "translateY(0)" : "translateY(14px)",
                transition: "opacity 0.6s ease, transform 0.6s ease",
              }}
            >
              <p style={{
                fontFamily: NU, fontSize: 10, fontWeight: 700,
                letterSpacing: "0.32em", textTransform: "uppercase",
                color: GOLD, margin: "0 0 22px",
              }}>
                The Wedding Atlas
              </p>
              <h1 style={{
                fontFamily: GD, fontSize: isMobile ? 46 : 76,
                fontWeight: 400, color: TXT,
                margin: "0 0 32px", lineHeight: 1.05,
                letterSpacing: "-0.02em",
              }}>
                Wedding Atlas
              </h1>
              <p style={{
                fontFamily: NU, fontSize: isMobile ? 15 : 17,
                color: dim(0.62), lineHeight: 1.75,
                maxWidth: 560, margin: "0 0 44px",
              }}>
                Every destination guide in the collection. Curated intelligence on
                venues, seasons, costs, and travel for couples planning a wedding
                worth the journey.
              </p>
              {/* Full-width editorial rule, separates intro from stats */}
              <div style={{
                height: 1,
                background: `linear-gradient(90deg, ${GOLD} 0%, rgba(196,163,90,0.22) 55%, transparent 100%)`,
                margin: "44px 0 0",
              }} />
            </div>

            {/* Stats strip + collection link row */}
            <div
              ref={statsRef}
              style={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "flex-end",
                justifyContent: "space-between",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? 28 : 0,
                flexWrap: "wrap",
                paddingTop: isMobile ? 32 : 40,
                opacity: statsVisible ? 1 : 0,
                transform: statsVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.55s ease 0.15s, transform 0.55s ease 0.15s",
              }}
            >
              {/* Stats */}
              <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 28 : 56, flexWrap: "wrap" }}>
                {[
                  { value: loading ? "-" : guides.length, label: "Destinations" },
                  { value: loading ? "-" : countries.length, label: "Countries"  },
                  { value: "Growing",                        label: "Collection"  },
                ].map(({ value, label }, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{
                      fontFamily: GD, fontSize: isMobile ? 34 : 44,
                      fontWeight: 400, color: TXT,
                      lineHeight: 1, letterSpacing: "-0.02em",
                    }}>
                      {value}
                    </span>
                    <span style={{
                      fontFamily: NU, fontSize: 10, fontWeight: 600,
                      letterSpacing: "0.22em", textTransform: "uppercase",
                      color: dim(0.38),
                    }}>
                      {label}
                    </span>
                    {i < 2 && !isMobile && (
                      <span style={{
                        fontFamily: NU, fontSize: 12, color: dim(0.15), marginLeft: 16,
                      }}>
                        /
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* View the full collection, bottom right of hero */}
              <HeroCollectionLink onClick={() => collectionRef.current?.scrollIntoView({ behavior: "smooth" })} />
            </div>
          </div>
        </section>

        {/* ── FEATURED DESTINATIONS ─────────────────────────────────────────── */}
        {!loading && featuredGuides.length > 0 && (
          <section ref={collectionRef} style={{
            background: BG_ALT,
            padding: isMobile ? "64px 24px 72px" : "80px 64px 96px",
          }}>
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
              <FeaturedHeader isMobile={isMobile} />
              <div style={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols3}, 1fr)`,
                gap: isMobile ? 40 : 32,
              }}>
                {featuredGuides.map((g, i) => (
                  <EditorialCard
                    key={g.slug}
                    collection="atlas"
                    contentType="region_guide"
                    contentId={g.slug}
                    title={g.name}
                    category={g.country}
                    country={g.country}
                    image={g.hero_image}
                    onNavigate={navigate}
                    source="wedding_guides"
                    context="featured"
                    position={i}
                    large
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── COUNTRY SECTIONS ──────────────────────────────────────────────── */}
        {!loading && countries.map(country => {
          const countryGuides = byCountry[country];
          const total         = countryGuides.length;
          const isExpanded    = !!expandedCountries[country];
          const visibleGuides = (total <= PREVIEW_COUNT || isExpanded)
            ? countryGuides
            : countryGuides.slice(0, PREVIEW_COUNT);
          const ctxSlug = COUNTRY_ATLAS_SLUG[country] || country.toLowerCase().replace(/\s+/g, "_");

          return (
            <section key={country} style={{
              padding: isMobile ? "64px 24px 72px" : "80px 64px 96px",
              boxSizing: "border-box",
            }}>
              <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <CountryHeader country={country} count={total} isMobile={isMobile} />
                <div style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${cols3}, 1fr)`,
                  gap: isMobile ? 40 : 28,
                }}>
                  {visibleGuides.map((g, i) => (
                    <EditorialCard
                      key={g.slug}
                      collection="atlas"
                      contentType="region_guide"
                      contentId={g.slug}
                      title={g.name}
                      category={g.country}
                      country={g.country}
                      image={g.hero_image}
                      onNavigate={navigate}
                      source="wedding_guides"
                      context={`${ctxSlug}_grid`}
                      position={i}
                    />
                  ))}
                </div>
                {total > PREVIEW_COUNT && (
                  <CountryCTAs
                    country={country}
                    total={total}
                    isExpanded={isExpanded}
                    onExpand={() => expandCountry(country)}
                    onCollapse={() => collapseCountry(country)}
                    isMobile={isMobile}
                  />
                )}
              </div>
            </section>
          );
        })}

        {/* ── LOADING SKELETON ──────────────────────────────────────────────── */}
        {loading && (
          <div style={{ padding: isMobile ? "0 24px 96px" : "0 64px 96px" }}>
            <div style={{
              maxWidth: 1200, margin: "0 auto",
              display: "grid",
              gridTemplateColumns: `repeat(${cols3}, 1fr)`,
              gap: 28,
            }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{
                  aspectRatio: "3/4",
                  background: "#EDE9E1",
                  animation: "atlas-pulse 1.5s ease infinite",
                  animationDelay: `${i * 100}ms`,
                }} />
              ))}
            </div>
          </div>
        )}

        <div style={{ height: 64, background: BG }} />
      </div>

      <style>{`
        @keyframes atlas-pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </>
  );
}
